/**
 * Isometric coordinate math for 2:1 ratio projection.
 *
 * Grid coordinates (col, row) represent tile positions.
 * World coordinates (x, y) are pixel positions in isometric space.
 * Screen coordinates factor in camera pan/zoom.
 */

/** Full isometric diamond width in pixels. */
export const TILE_WIDTH = 64;

/** Full isometric diamond height in pixels (2:1 ratio). */
export const TILE_HEIGHT = 32;

/** Convert grid coordinates to world pixel position (top of diamond). */
export function gridToWorld(col: number, row: number): { x: number; y: number } {
  return {
    x: (col - row) * (TILE_WIDTH / 2),
    y: (col + row) * (TILE_HEIGHT / 2),
  };
}

/** Convert world pixel position to approximate grid coordinates. */
export function worldToGrid(wx: number, wy: number): { col: number; row: number } {
  return {
    col: Math.round(wx / TILE_WIDTH + wy / TILE_HEIGHT),
    row: Math.round(wy / TILE_HEIGHT - wx / TILE_WIDTH),
  };
}

/**
 * Compute depth sort key for painter's algorithm (back-to-front).
 * Higher values are drawn later (on top).
 *
 * Layer values:
 *   0 = floor tiles
 *   1 = furniture
 *   2 = characters
 *   3 = effects / particles
 */
export function depthKey(col: number, row: number, layer: number = 0): number {
  return (col + row) * 100 + layer;
}

/** Get the bounding box of an isometric tile diamond in world space. */
export function tileBounds(col: number, row: number) {
  const { x, y } = gridToWorld(col, row);
  return {
    top: y,
    bottom: y + TILE_HEIGHT,
    left: x - TILE_WIDTH / 2,
    right: x + TILE_WIDTH / 2,
  };
}
